import { storage } from '@/lib/storage';
import { Product } from '@/types';
import { nanoid } from 'nanoid';
import { priceHistoryService } from './priceHistoryService';
import { batchService } from './batchService';
import {
  getProductSalesCategory,
  isLowStockProduct,
  isTireCategory,
  normalizeProductCategory,
} from '@/lib/catalogCategories';

const PRODUCTS_KEY = 'products';

export const productService = {
  getAll: (): Product[] => {
    return (storage.get<Product[]>(PRODUCTS_KEY) || []).map((product) => {
      const category = normalizeProductCategory(product.category);
      return {
        ...product,
        category,
        sales_category: product.sales_category ?? getProductSalesCategory(category),
      };
    });
  },

  getById: (id: string): Product | null => {
    const products = productService.getAll();
    return products.find(p => p.id === id) || null;
  },

  search: (query: string): Product[] => {
    const products = productService.getAll();
    const lowerQuery = query.toLowerCase();
    return products.filter(p =>
      p.name.toLowerCase().includes(lowerQuery) ||
      p.sku.toLowerCase().includes(lowerQuery) ||
      (p.barcode && p.barcode.toLowerCase().includes(lowerQuery))
    );
  },

  getByCategory: (category: string): Product[] => {
    const products = productService.getAll();
    if (category === 'all') return products;
    if (category === 'low-stock') {
      return products.filter(isLowStockProduct);
    }
    return products.filter(p => p.category === category);
  },

  create: (data: Omit<Product, 'id' | 'created_at' | 'updated_at'>): Product => {
    const products = productService.getAll();
    const newProduct: Product = {
      ...data,
      category: normalizeProductCategory(data.category),
      sales_category: data.sales_category ?? getProductSalesCategory(normalizeProductCategory(data.category)),
      id: nanoid(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    products.push(newProduct);
    storage.set(PRODUCTS_KEY, products);
    return newProduct;
  },

  update: (id: string, data: Partial<Product>): Product | null => {
    const products = productService.getAll();
    const index = products.findIndex(p => p.id === id);
    if (index === -1) return null;
    
    const oldProduct = products[index];
    
    // Record cost price change
    if (data.cost_price !== undefined && data.cost_price !== oldProduct.cost_price) {
      priceHistoryService.recordCostChange(
        id,
        oldProduct.cost_price,
        data.cost_price,
        'manual_adjustment'
      );
    }
    
    // Record selling price change
    if (data.selling_price !== undefined && data.selling_price !== oldProduct.selling_price) {
      priceHistoryService.recordPriceChange(
        id,
        oldProduct.selling_price,
        data.selling_price,
        'manual_adjustment'
      );
    }
    
    const nextCategory = data.category
      ? normalizeProductCategory(data.category)
      : products[index].category;
    products[index] = {
      ...products[index],
      ...data,
      category: nextCategory,
      sales_category: data.sales_category ?? getProductSalesCategory(nextCategory),
      id: products[index].id,
      created_at: products[index].created_at,
      updated_at: new Date().toISOString(),
    };
    storage.set(PRODUCTS_KEY, products);
    return products[index];
  },

  delete: (id: string): boolean => {
    const products = productService.getAll();
    const filtered = products.filter(p => p.id !== id);
    if (filtered.length === products.length) return false;
    storage.set(PRODUCTS_KEY, filtered);
    return true;
  },

  adjustStock: (id: string, quantity: number): boolean => {
    const product = productService.getById(id);
    if (!product) return false;
    const newStock = product.current_stock + quantity;
    if (newStock < 0) return false;
    productService.update(id, { current_stock: newStock });
    return true;
  },

  adjustStockWithReason: (id: string, quantity: number, adjustmentType: string, reason: string): { success: boolean; previousStock: number; newStock: number } => {
    const product = productService.getById(id);
    if (!product) return { success: false, previousStock: 0, newStock: 0 };
    
    const previousStock = product.current_stock;
    const newStock = previousStock + quantity;
    
    if (newStock < 0) return { success: false, previousStock, newStock: previousStock };
    
    productService.update(id, { current_stock: newStock });
    return { success: true, previousStock, newStock };
  },

  receiveStockWithCost: (
    id: string, 
    quantity: number, 
    unitCost: number, 
    reason: string,
    productionDate?: string,
    batchNumber?: string
  ): { 
    success: boolean; 
    previousStock: number; 
    newStock: number;
    previousCost: number;
    newCost: number;
    batchId?: string;
  } => {
    const product = productService.getById(id);
    if (!product || quantity <= 0 || unitCost <= 0) {
      return { 
        success: false, 
        previousStock: 0, 
        newStock: 0,
        previousCost: 0,
        newCost: 0
      };
    }
    
    const previousStock = product.current_stock;
    const previousCost = product.cost_price;
    const newStock = previousStock + quantity;
    
    // Calculate weighted average cost
    const totalCostBefore = previousStock * previousCost;
    const newCostAdded = quantity * unitCost;
    const newAverageCost = (totalCostBefore + newCostAdded) / newStock;
    
    // Create batch if product is tire
    let batchId: string | undefined;
    if (isTireCategory(product.category)) {
      const batch = batchService.createBatch(
        id,
        quantity,
        unitCost,
        productionDate,
        batchNumber
      );
      batchId = batch.id;
    }
    
    // Record price history
    if (newAverageCost !== previousCost) {
      priceHistoryService.recordCostChange(
        id,
        previousCost,
        newAverageCost,
        'manual_adjustment',
        reason
      );
    }
    
    // Update product
    productService.update(id, {
      current_stock: newStock,
      cost_price: newAverageCost,
      batch_tracking_enabled: isTireCategory(product.category),
      average_cost_tracking: {
        total_quantity: newStock,
        total_cost: totalCostBefore + newCostAdded,
        last_updated: new Date().toISOString()
      }
    });
    
    return {
      success: true,
      previousStock,
      newStock,
      previousCost,
      newCost: newAverageCost,
      batchId
    };
  },
};
